import { AwardIcon } from '@/assets/icons/award-icon'
import { ClipboardIcon } from '@/assets/icons/clipboard-icon'
import { HandIcon } from '@/assets/icons/hand-icon'
import { AUTO_GRADING_PLANS } from '@/constants/plans/variables-plans'
import { ReactComponent as RuleIcon } from '@/assets/icons/ruler-white.svg'

/* eslint-disable @typescript-eslint/naming-convention */
export interface CardActivityWithId extends CardActivity {
  id: string
}

export const enum FeedbackTypesCard {
  SUGGESTED_RESPONSE = 'suggested_response',
  WIDA = 'wida',
  GRAMMAR_INSIGHTS = 'grammar_insights',
  ACTFL = 'actfl',
  PROFICIENCY_LEVEL = 'proficiency_level',
}

export const enum LeniencyCard {
  CONFIDENCE = 'confidence',
  EASY = 'easy',
  NORMAL = 'normal',
  HARD = 'hard',
}

export interface CardActivity {
  owners: string[]
  checked?: boolean
  completed?: boolean
  media_area_id?: string | null
  media_area_layout?: 'left' | 'right' | null
  score?: number
  verificationStatus?: 'VERIFIED' | 'WARNING' | 'NOT_RECOMMENDED' | 'NOT_WORKING' | 'NOT_CHECKED' | 'UNKNOWN' | null
  native_text?: string
  repeat?: number
  language?: string | null
  image?: {
    path?: string | null
    url?: string
  }
  audio?: {
    path?: string | null
    url?: string | null
  }
  correct_answer?: string
  notes?: string
  difficulty?: LeniencyCard
  default_language?: string
  target_text?: string
  type?: CardActivityType
  grading_criteria?: string
  grading_criteria_title?: string
  scoring_type?: string
  grading_method?: 'simple' | 'rubric' | 'manual' | 'standards_based'
  feedback_types?: FeedbackTypesCard[]
  rubricId?: string
  prompt?: string
  title?: string
  passing_score?: number
  maxCharacters?: number
  answer?: string[]
  choices?: {
    value: string
    option: string
  }[]
  MCQType?: 'single' | 'multi'
  multipleAttemptsAllowed?: boolean
  allowRetries?: boolean
  allowTTS?: boolean
  question?: string
  respondTime?: number
  hidePrompt?: boolean
  videoUrl?: string
  link?: string
  text?: string
  isListenAloud?: boolean
  embedCode?: string
  attempt?: number
  correct?: number
  autoGrade?: boolean
  feedback_language?: 'native' | 'target'
  points?: number
  shuffle?: boolean
  translation?: string
  includeAIContext?: boolean
  media_area_context_ref?: string | null
  standardId?: string
  target_proficiency_level?: string
}

export const enum CardActivityType {
  // DEFAULT = 'READ_REPEAT',
  READ_REPEAT = 'READ_REPEAT',
  VIDEO = 'VIDEO',
  TEXT = 'TEXT',
  READ_RESPOND = 'READ_RESPOND',
  FREE_RESPONSE = 'FREE_RESPONSE',
  REPEAT = 'REPEAT',
  RESPOND = 'RESPOND',
  RESPOND_WRITE = 'RESPOND_WRITE',
  TEXT_TO_SPEECH = 'TEXT_TO_SPEECH',
  MULTIPLE_CHOICE = 'MULTIPLE_CHOICE',
  PODCAST = 'PODCAST',
  MEDIA_PAGE = 'MEDIA_PAGE',
  WRITE = 'WRITE',
  SHORT_ANSWER = 'SHORT_ANSWER',
  SHORT_STORY = 'SHORT_STORY',
  SPEAK = 'SPEAK',
  CONVERSATION = 'CONVERSATION',
  CONVERSATION_WRITE = 'CONVERSATION_WRITE',
  DIALOGUE = 'DIALOGUE',
  INSTRUCTION = 'INSTRUCTION',
  LISTEN = 'LISTEN',
  READ = 'READ',
  ANSWER = 'ANSWER',
}

export const RESPOND_CARD_ACTIVITY_TYPES = [
  CardActivityType.READ_RESPOND,
  CardActivityType.RESPOND,
  CardActivityType.RESPOND_WRITE,
  CardActivityType.FREE_RESPONSE,
]

export const SHORT_ANSWER_CARD_ACTIVITY_TYPES = [CardActivityType.SHORT_ANSWER]

export const BASE_RESPOND_FIELD_VALUES = {
  title: '',
  allowRetries: true,
  respondTime: 180,
  maxCharacters: 1000,
}

export const BASE_REPEAT_FIELD_VALUES = {
  repeat: 1,
}

export const BASE_SHORT_ANSWER_FIELD_VALUES = {
  maxCharacters: 100,
  allowRetries: true,
  correct_answer: '',
}

export const BASE_MULTIPLE_CHOICE_FIELD_VALUES = {
  MCQType: 'single',
  answer: ['A'],
  choices: [
    { option: 'A', value: 'Option A' },
    { option: 'B', value: 'Option B' },
    { option: 'C', value: 'Option C' },
  ],
}

export const FEEDBACKS_CARD_TYPES = [
  FeedbackTypesCard.SUGGESTED_RESPONSE,
  FeedbackTypesCard.WIDA,
  FeedbackTypesCard.GRAMMAR_INSIGHTS,
  FeedbackTypesCard.ACTFL,
]

export const LENIENCY_OPTIONS = [
  {
    label: 'Build Confidence - most lenient',
    value: LeniencyCard.CONFIDENCE,
  },
  {
    label: 'Very Lenient',
    value: LeniencyCard.EASY,
  },
  {
    label: 'Normal',
    value: LeniencyCard.NORMAL,
  },
  {
    label: 'No leniency - most strict',
    value: LeniencyCard.HARD,
  },
]

export const MC_QUESTION_TYPE_OPTIONS = [
  { name: 'Multi-Check', label: 'Multi-Check', value: 'multi' },
  { name: 'Single-Select', label: 'Single-Select', value: 'single' },
]

export const REPEAT_AUDIO_OPTIONS = [
  {
    label: 'Text-to-Speech',
    value: 'tts',
  },
  {
    label: 'Record Your Voice',
    value: 'audio',
  },
]

export const GRADING_METHOD_OPTIONS = [
  {
    title: 'Pass-Fail',
    description: 'You Set The Bar',
    detailedTitle: 'Pass-Fail',
    icon: AwardIcon,
    value: 'simple',
    bg: '#66C61C',
    permission: [], //AUTO_GRADING_PLANS.AUTO_GRADING_PASS_FAIL,
  },
  {
    title: 'Standards Based',
    description: 'Assess By A Standard',
    detailedTitle: 'Standards Based',
    icon: RuleIcon,
    value: 'standards_based',
    bg: '#FF9C66',
    permission: [], //    AUTO_GRADING_PLANS.AUTO_GRADING_STANDARDS_BASED,
  },
  {
    title: 'Rubric',
    description: 'Detailed Scoring',
    detailedTitle: 'Rubric Grading',
    icon: ClipboardIcon,
    value: 'rubric',
    bg: '#7A5AF8',
    permission: [], //AUTO_GRADING_PLANS.AUTO_GRADING_RUBRICS,
  },
  {
    title: 'Manual',
    description: 'Grade Responses Yourself',
    detailedTitle: 'Manual',
    icon: HandIcon,
    value: 'manual',
    bg: '#47CD89',
    permission: [], //AUTO_GRADING_PLANS.AUTO_GRADING_MANUAL,
  },
]

export const STUDENT_LEVELS_OPTIONS = [
  {
    label: 'Beginner',
    description: 'Beginner Level: Just starting out. Can say a few basic words and phrases.',
    value: 'beginner',
  },
  {
    label: 'Elementary',
    description: 'Elementary Level: Can understand simple sentences and have very basic conversations.',
    value: 'elementary',
  },
  {
    label: 'Intermediate',
    description: 'Intermediate Level: Can talk about everyday topics and handle common situations.',
    value: 'intermediate',
  },
  {
    label: 'Advanced',
    description: 'Advanced Level: Can speak and understand with ease, and explain ideas clearly.',
    value: 'advanced',
  },
  {
    label: 'Fluent',
    description: 'Fluent Level: Speaks naturally and easily. Can use the language in work or school settings.',
    value: 'fluent',
  },
  {
    label: 'Native-like',
    description: 'Native-like Level: Understands and speaks like a native. Can discuss complex ideas accurately.',
    value: 'nativeLike',
  },
]

export const RESPOND_CARD_WRITTEN_ACTIVITY_TYPES = [CardActivityType.RESPOND_WRITE, CardActivityType.FREE_RESPONSE]

export const RESPOND_CARD_AUDIO_ACTIVITY_TYPES = [CardActivityType.RESPOND, CardActivityType.READ_RESPOND]

export const MULTIPLE_CHOICE_CARD_ACTIVITY_TYPES = [CardActivityType.MULTIPLE_CHOICE]

export const REPEAT_CARD_ACTIVITY_TYPES = [CardActivityType.READ_REPEAT, CardActivityType.REPEAT]

export const getFeedbackLabel = (feedback: FeedbackTypesCard, showTypeProficiency = false) => {
  if (feedback === FeedbackTypesCard.SUGGESTED_RESPONSE) {
    return 'Suggested Response'
  }
  if (feedback === FeedbackTypesCard.WIDA) {
    return !showTypeProficiency ? 'Proficiency Level' : 'WIDA Level'
  }
  if (feedback === FeedbackTypesCard.GRAMMAR_INSIGHTS) {
    return 'Grammar Insights'
  }
  if (feedback === FeedbackTypesCard.ACTFL) {
    return !showTypeProficiency ? 'Proficiency Level' : 'ACTFL Level'
  }
}
